import React, { useState, useEffect } from "react";
import { ShieldCheck, Search, Clock, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Funnel } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import authHeader from "../../../Auth/authHeader";
import { ApiLink } from "../../../APILink/ApiLink";
import "./AuditLogsTab.scss";

export default function AuditLogsTab({ companyId, fiscalYear }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionTypes, setActionTypes] = useState([]);
  const [users, setUsers] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    actionType: "",
    createdByUser: "",
    sortBy: "CreatedDate",
    sortDir: "desc",
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!companyId || !fiscalYear) {
      setLogs([]);
      setLoading(false);
      return;
    }
    loadLogs();
  }, [companyId, fiscalYear, searchTerm, filters.actionType, filters.createdByUser, filters.sortBy, filters.sortDir, pagination.page, pagination.pageSize]);

  const loadLogs = async () => {
    try {
      setLoading(true);

      const headers = authHeader();
      const response = await axios.get(ApiLink.API_URL + "AuditLogs/paged", {
        headers,
        params: {
          companyId,
          fiscalYear,
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: searchTerm,
          actionType: filters.actionType,
          createdByUser: filters.createdByUser,
          sortBy: filters.sortBy,
          sortDir: filters.sortDir,
        },
      });

      setLogs(response?.data?.items || []);
      setPagination((prev) => ({
        ...prev,
        page: response?.data?.pagination?.page || prev.page,
        pageSize: response?.data?.pagination?.pageSize || prev.pageSize,
        totalCount: response?.data?.pagination?.totalCount || 0,
        totalPages: response?.data?.pagination?.totalPages || 0,
      }));
      setActionTypes(response?.data?.filters?.actionTypes || []);
      setUsers(response?.data?.filters?.users || []);
    } catch (error) {
      console.error("Failed to load audit logs", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const totalPages = pagination.totalPages || 0;
  const currentPage = pagination.page || 1;

  const getVisiblePages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  return (
    <div className="audit-logs-tab">
      <div className="audit-logs-tab__header">
        <div className="audit-logs-tab__title-wrap">
          <h2 className="audit-logs-tab__title">
            <ShieldCheck className="audit-logs-tab__title-icon" />
            Regjistri i Auditimit te Sistemit
          </h2>
          <p className="audit-logs-tab__subtitle">
            Ndiq te gjitha ndryshimet e konfigurimit dhe ngjarjet e sistemit per perputhshmeri.
          </p>
        </div>
        
        <div className="audit-logs-tab__search-wrap">
          <Search className="audit-logs-tab__search-icon" />
          <input 
            placeholder="Kerko ne logje..." 
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="audit-logs-tab__search-input"
          />
        </div>
      </div>

      <div className="audit-logs-tab__filters-bar">
        <div className="audit-logs-tab__filter-head">
          <Funnel className="audit-logs-tab__filter-head-icon" />
          Filtra
        </div>

        <select
          className="audit-logs-tab__filter-select"
          value={filters.actionType}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, actionType: e.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <option value="">Te gjitha ActionType</option>
          {actionTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <select
          className="audit-logs-tab__filter-select"
          value={filters.createdByUser}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, createdByUser: e.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <option value="">Te gjithe perdoruesit</option>
          {users.map((user) => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>

        <select
          className="audit-logs-tab__filter-select"
          value={filters.sortBy}
          onChange={(e) => setFilters((prev) => ({ ...prev, sortBy: e.target.value }))}
        >
          <option value="CreatedDate">Rendit sipas dates</option>
          <option value="LogID">Rendit sipas LogID</option>
          <option value="ActionType">Rendit sipas ActionType</option>
          <option value="CreatedByUser">Rendit sipas perdoruesit</option>
        </select>

        <select
          className="audit-logs-tab__filter-select"
          value={filters.sortDir}
          onChange={(e) => setFilters((prev) => ({ ...prev, sortDir: e.target.value }))}
        >
          <option value="desc">Me te rejat</option>
          <option value="asc">Me te vjetrat</option>
        </select>

        <select
          className="audit-logs-tab__filter-select audit-logs-tab__filter-select--small"
          value={pagination.pageSize}
          onChange={(e) => setPagination((prev) => ({ ...prev, pageSize: Number(e.target.value), page: 1 }))}
        >
          <option value={10}>10 / faqe</option>
          <option value={20}>20 / faqe</option>
          <option value={50}>50 / faqe</option>
          <option value={100}>100 / faqe</option>
        </select>
      </div>

      <div className="audit-logs-tab__table-card">
        {loading ? (
          <div className="audit-logs-tab__state audit-logs-tab__state--loading">
            <Clock className="audit-logs-tab__loading-icon" />
            Duke ngarkuar historikun e auditimit...
          </div>
        ) : logs.length === 0 ? (
          <div className="audit-logs-tab__state audit-logs-tab__state--empty">
            <ShieldCheck className="audit-logs-tab__empty-icon" />
            <h3 className="audit-logs-tab__empty-title">Nuk u gjeten logje</h3>
            <p className="audit-logs-tab__empty-subtitle">Provo te ndryshosh termat e kerkimit.</p>
          </div>
        ) : (
          <div className="audit-logs-tab__table-wrap">
            <table className="audit-logs-tab__table">
              <thead className="audit-logs-tab__thead">
                <tr>
                  <th scope="col" className="audit-logs-tab__th">LogID</th>
                  <th scope="col" className="audit-logs-tab__th">Koha</th>
                  <th scope="col" className="audit-logs-tab__th">TransactionID</th>
                  <th scope="col" className="audit-logs-tab__th">Perdoruesi</th>
                  <th scope="col" className="audit-logs-tab__th">ActionType</th>
                  <th scope="col" className="audit-logs-tab__th">Action</th>
                  <th scope="col" className="audit-logs-tab__th">Pershkrimi</th>
                </tr>
              </thead>
              <tbody className="audit-logs-tab__tbody">
                {logs.map((log) => (
                  <tr key={log.logId} className="audit-logs-tab__row">
                    <td className="audit-logs-tab__td">{log.logId}</td>
                    <td className="audit-logs-tab__td audit-logs-tab__td--time">
                      <div className="audit-logs-tab__time-wrap">
                        <Clock className="audit-logs-tab__time-icon" />
                        {log.createdDate ? format(new Date(log.createdDate), "dd.MM.yyyy HH:mm:ss") : "Date e panjohur"}
                      </div>
                    </td>
                    <td className="audit-logs-tab__td">{log.transactionId ?? "-"}</td>
                    <td className="audit-logs-tab__td">
                      <div className="audit-logs-tab__user-wrap">
                        <div className="audit-logs-tab__avatar">
                          {log.createdByUser ? log.createdByUser.charAt(0).toUpperCase() : "?"}
                        </div>
                        <span className="audit-logs-tab__user-email">{log.createdByUser || "-"}</span>
                      </div>
                    </td>
                    <td className="audit-logs-tab__td">
                      <span className="audit-logs-tab__category-pill">
                        {log.actionType || "-"}
                      </span>
                    </td>
                    <td className="audit-logs-tab__td audit-logs-tab__td--details">{log.action || "-"}</td>
                    <td className="audit-logs-tab__td audit-logs-tab__td--details">
                      <div className="audit-logs-tab__details" title={log.description}>{log.description || "-"}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 0 && (
          <div className="audit-logs-tab__pagination audit-logs-tab__pagination--footer">
            <div className="audit-logs-tab__pagination-info">
              Totali: {pagination.totalCount} rreshta | Faqja {currentPage} / {totalPages}
            </div>
            <div className="audit-logs-tab__pagination-actions">
              <button type="button" onClick={() => goToPage(1)} disabled={currentPage <= 1} className="audit-logs-tab__page-btn audit-logs-tab__page-btn--wide">
                <ChevronsLeft className="audit-logs-tab__page-icon" />
                <span>Fillim</span>
              </button>
              <button type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className="audit-logs-tab__page-btn audit-logs-tab__page-btn--wide">
                <ChevronLeft className="audit-logs-tab__page-icon" />
                <span>Mbrapa</span>
              </button>

              <div className="audit-logs-tab__page-numbers" aria-label="Page numbers">
                {getVisiblePages().map((page, idx) => (
                  page === "..." ? (
                    <span key={`dots-${idx}`} className="audit-logs-tab__page-dots">...</span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      onClick={() => goToPage(page)}
                      className={`audit-logs-tab__page-number ${page === currentPage ? "is-active" : ""}`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              <button type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages} className="audit-logs-tab__page-btn audit-logs-tab__page-btn--wide">
                <span>Para</span>
                <ChevronRight className="audit-logs-tab__page-icon" />
              </button>
              <button type="button" onClick={() => goToPage(totalPages)} disabled={currentPage >= totalPages} className="audit-logs-tab__page-btn audit-logs-tab__page-btn--wide">
                <span>Fund</span>
                <ChevronsRight className="audit-logs-tab__page-icon" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}